import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { supabase } from '../lib/supabase'
import CampoSenha from '../componentes/CampoSenha'
import CartaoErro from '../componentes/CartaoErro'
import { LINK_VENCIDO, validarNovaSenha } from '../auth/recuperacaoDeSenha'

// Quem chegou pelo CONVITE da compra (Hotmart) nunca teve senha: "criada" e
// segue para o Cadastro inicial. Quem pediu "Esqueci minha senha": "trocada".
// Decide pelo tipo do link (?type= no link novo, #type= no antigo).
function veioDoConvite(): boolean {
  return /[?#&]type=invite(&|$)/.test(window.location.search + window.location.hash)
}

// Desde 15/10 o botao dos e-mails (convite e redefinir senha) aponta para o
// PROPRIO app -- /nova-senha?token_hash=...&type=invite|recovery -- e nao para
// o endereco do Supabase: link de outro dominio pesa para o e-mail cair no
// spam. O app confere o codigo aqui. Bonus: o filtro de e-mail que "abre" o
// link para olhar nao gasta o codigo (so este JavaScript gasta).
function lerCodigoDoLink(): { tokenHash: string; tipo: 'invite' | 'recovery' } | null {
  const q = new URLSearchParams(window.location.search)
  const tokenHash = q.get('token_hash')
  const tipo = q.get('type')
  if (!tokenHash || (tipo !== 'invite' && tipo !== 'recovery')) return null
  return { tokenHash, tipo }
}

export default function NovaSenha() {
  const { session, carregando, salvarNovaSenha } = useAuth()
  const navegar = useNavigate()
  const [senha, setSenha] = useState('')
  const [repeticao, setRepeticao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [pronto, setPronto] = useState(false)
  // Link vencido ou ja usado: o Supabase volta com #error_code=otp_expired.
  // Lido na primeira tela, porque quem ja estava conectado neste navegador
  // tem sessao e, sem isto, veria o formulario como se o link valesse.
  const [linkComErro] = useState(() => window.location.hash.includes('error_code='))
  const [codigo] = useState(lerCodigoDoLink)
  const [convite] = useState(veioDoConvite)
  const [conferencia, setConferencia] = useState<'conferindo' | 'ok' | 'vencido'>(
    codigo ? 'conferindo' : 'ok',
  )
  const jaConferiu = useRef(false)

  useEffect(() => {
    if (!codigo || jaConferiu.current) return
    jaConferiu.current = true // o codigo vale uma vez so: nunca conferir duas
    supabase.auth.verifyOtp({ token_hash: codigo.tokenHash, type: codigo.tipo }).then(({ error }) => {
      window.history.replaceState(null, '', '/nova-senha')
      setConferencia(error ? 'vencido' : 'ok')
    })
  }, [codigo])

  async function aoEnviar(e: FormEvent) {
    e.preventDefault()
    // Confere aqui antes de gastar uma ida ao servidor -- e antes de gastar
    // o link, que so vale uma vez.
    const reclamacao = validarNovaSenha(senha, repeticao)
    if (reclamacao) {
      setErro(reclamacao)
      return
    }
    setSalvando(true)
    setErro(null)
    const mensagem = await salvarNovaSenha(senha)
    setSalvando(false)
    if (mensagem) setErro(mensagem)
    else setPronto(true)
  }

  const moldura = 'min-h-screen flex items-center justify-center bg-slate-100 p-4'
  const cartao = 'w-full max-w-sm bg-white rounded-xl shadow-sm p-6 space-y-4'

  if (carregando || conferencia === 'conferindo') {
    return (
      <main className={moldura}>
        <div className={cartao}>
          <p className="text-sm text-slate-600">Abrindo…</p>
        </div>
      </main>
    )
  }

  // Link vencido, ja usado, ou aberto direto sem passar pelo e-mail: nao
  // existe sessao para trocar a senha de ninguem. Esta tela precisa ficar
  // FORA da area protegida justamente para poder dizer isto -- protegida, a
  // pessoa seria jogada para /login sem ler explicacao nenhuma.
  if (!session || linkComErro || conferencia === 'vencido') {
    return (
      <main className={moldura}>
        <div className={cartao}>
          <h1 className="text-xl font-bold text-slate-800">Link vencido</h1>
          <p className="text-sm text-slate-700">{LINK_VENCIDO}</p>
          <Link
            to="/esqueci-senha"
            className="block text-center bg-teal-700 text-white rounded-lg py-2 font-medium"
          >
            Pedir um link novo
          </Link>
        </div>
      </main>
    )
  }

  if (pronto) {
    return (
      <main className={moldura}>
        <div className={cartao}>
          <h1 className="text-xl font-bold text-slate-800">{convite ? 'Senha criada' : 'Senha trocada'}</h1>
          {/* ⚠ O passo que a pessoa nao adivinha: o link do e-mail abriu no
              NAVEGADOR, e o app da tela inicial e outro lugar -- no iPhone eles
              nem compartilham o login. Sem esta frase ela troca a senha, ve o
              app funcionando aqui, e nao entende por que o icone continua
              pedindo senha. */}
          <p className="text-sm text-slate-700">
            Pronto. Agora abra o <strong>app na tela inicial do celular</strong> e entre com a
            senha nova.
          </p>
          <button
            type="button"
            // Comprador novo (convite) vai ao Cadastro inicial, como era no cadastro aberto.
            onClick={() => navegar(convite ? '/perfil?inicio=1' : '/')}
            className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium"
          >
            Continuar aqui mesmo
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className={moldura}>
      <form onSubmit={aoEnviar} className={cartao}>
        <h1 className="text-xl font-bold text-slate-800">Criar uma senha nova</h1>
        {/* Cada campo tem o SEU olho: revelar a nova senha nao revela a
            repeticao, senao conferir uma contra a outra perde a graca. */}
        <CampoSenha
          id="senha"
          rotulo="Nova senha"
          valor={senha}
          aoMudar={setSenha}
          autoComplete="new-password"
        />
        <CampoSenha
          id="repeticao"
          rotulo="Repita a nova senha"
          valor={repeticao}
          aoMudar={setRepeticao}
          autoComplete="new-password"
        />
        {erro && <CartaoErro titulo="Não foi possível salvar a senha">{erro}</CartaoErro>}
        <button
          type="submit"
          disabled={salvando}
          className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50"
        >
          {salvando ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>
    </main>
  )
}
